import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';

// Standard aspect ratios for the app (matching mobile)
export const IMAGE_FORMATS = {
  PROFILE: { aspectRatio: [1, 1], maxSize: 400, shape: 'circle' },
  SERVICE: { aspectRatio: [16, 9], maxSize: 800, shape: 'rectangle' },
  COURSE: { aspectRatio: [16, 9], maxSize: 800, shape: 'rectangle' },
  COVER: { aspectRatio: [3, 2], maxSize: 1000, shape: 'rectangle' },
};

// Translations for the component
const translations = {
  es: {
    processing: 'Procesando...',
    change: 'Cambiar',
    cameraOrLibrary: 'Cámara o Galería',
    cameraOrBrowse: 'Cámara o Buscar Archivos',
    addPhoto: 'Añadir Foto',
    takePhoto: 'Tomar Foto',
    useWebcam: 'Usar Webcam',
    chooseFromLibrary: 'Elegir de Galería',
    browseFiles: 'Buscar Archivos',
    removePhoto: 'Eliminar Foto',
    capture: 'Capturar',
    cancel: 'Cancelar',
  },
  en: {
    processing: 'Processing...',
    change: 'Change',
    cameraOrLibrary: 'Camera or Photo Library',
    cameraOrBrowse: 'Camera or Browse Files',
    addPhoto: 'Add Photo',
    takePhoto: 'Take Photo',
    useWebcam: 'Use Webcam',
    chooseFromLibrary: 'Choose from Library',
    browseFiles: 'Browse Files',
    removePhoto: 'Remove Photo',
    capture: 'Capture',
    cancel: 'Cancel',
  }
};

export function ImageUpload({
  image,
  onImageChange,
  label = 'Add Image',
  aspectRatio = [16, 9],
  maxSize = 800,
  shape = 'rectangle',
  placeholder = 'Tap to add photo',
  language = 'es',
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Check if we're on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // Get translations
  const t = translations[language] || translations.es;

  const processImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate dimensions maintaining aspect ratio
          let width = maxSize;
          let height = Math.round(maxSize * (aspectRatio[1] / aspectRatio[0]));
          
          // For profile (circle), make it square
          if (shape === 'circle') {
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and crop to center
          const sourceAspect = img.width / img.height;
          const targetAspect = width / height;
          
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          
          if (sourceAspect > targetAspect) {
            sw = img.height * targetAspect;
            sx = (img.width - sw) / 2;
          } else {
            sh = img.width / targetAspect;
            sy = (img.height - sh) / 2;
          }
          
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
          
          // Convert to base64
          const base64 = canvas.toDataURL('image/jpeg', 0.7);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setShowOptions(false);
    try {
      const base64 = await processImage(file);
      onImageChange(base64);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error processing image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startCamera = async () => {
    setShowOptions(false);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: isMobile ? 'environment' : 'user' } 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
      setShowCamera(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsLoading(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions
    let width = maxSize;
    let height = Math.round(maxSize * (aspectRatio[1] / aspectRatio[0]));
    if (shape === 'circle') height = maxSize;
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw video frame
    const videoAspect = video.videoWidth / video.videoHeight;
    const targetAspect = width / height;
    
    let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
    
    if (videoAspect > targetAspect) {
      sw = video.videoHeight * targetAspect;
      sx = (video.videoWidth - sw) / 2;
    } else {
      sh = video.videoWidth / targetAspect;
      sy = (video.videoHeight - sh) / 2;
    }
    
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);
    
    const base64 = canvas.toDataURL('image/jpeg', 0.7);
    onImageChange(base64);
    stopCamera();
    setIsLoading(false);
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const removeImage = () => {
    onImageChange(null);
    setShowOptions(false);
  };

  const containerStyle = shape === 'circle' 
    ? 'w-36 h-36 rounded-full mx-auto' 
    : 'w-full aspect-video rounded-2xl';

  return (
    <div className="mb-5">
      {label && <label className="block text-sm font-semibold text-primary mb-2">{label}</label>}
      
      <div
        className={`${containerStyle} border-2 border-dashed border-gray-200 bg-cream-dark overflow-hidden cursor-pointer hover:border-gold transition-colors relative`}
        style={{ backgroundColor: '#F8F6F3' }}
        onClick={() => !isLoading && setShowOptions(true)}
        data-testid="image-upload-container"
      >
        {isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4A574', borderTopColor: 'transparent' }} />
            <span className="text-sm text-secondary mt-2">{t.processing}</span>
          </div>
        ) : image ? (
          <div className="relative w-full h-full group">
            <img 
              src={image} 
              alt="Uploaded" 
              className={`w-full h-full object-cover ${shape === 'circle' ? 'rounded-full' : ''}`}
            />
            <div className="absolute inset-x-0 bottom-0 bg-black/50 py-2 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-medium">{t.change}</span>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-5">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm mb-3">
              <Camera className="w-8 h-8 text-gold" style={{ color: '#D4A574' }} />
            </div>
            <span className="text-base font-semibold text-primary">{placeholder}</span>
            <span className="text-sm text-secondary mt-1">
              {isMobile ? t.cameraOrLibrary : t.cameraOrBrowse}
            </span>
          </div>
        )}
      </div>

      {/* Options Dialog */}
      <Dialog open={showOptions} onOpenChange={setShowOptions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.addPhoto}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            <Button
              onClick={startCamera}
              className="w-full justify-start gap-3 h-12 bg-cream hover:bg-cream-dark"
              variant="outline"
              data-testid="camera-option"
            >
              <Camera className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
              <span>{isMobile ? t.takePhoto : t.useWebcam}</span>
            </Button>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full justify-start gap-3 h-12 bg-cream hover:bg-cream-dark"
              variant="outline"
              data-testid="gallery-option"
            >
              <Upload className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
              <span>{isMobile ? t.chooseFromLibrary : t.browseFiles}</span>
            </Button>
            
            {image && (
              <Button
                onClick={removeImage}
                variant="destructive"
                className="w-full justify-start gap-3 h-12"
                data-testid="remove-image-option"
              >
                <X className="w-5 h-5" />
                <span>{t.removePhoto}</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.takePhoto}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-3">
              <Button
                onClick={stopCamera}
                variant="outline"
                className="flex-1"
              >
                {t.cancel}
              </Button>
              <Button
                onClick={capturePhoto}
                className="flex-1 bg-gold hover:bg-gold/90 text-white"
                style={{ backgroundColor: '#D4A574' }}
                data-testid="capture-photo-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                {t.capture}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture={isMobile ? 'environment' : undefined}
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
    </div>
  );
}

export default ImageUpload;
